import type { AccessGroupRow } from '@/features/configuracoes/api';
import { Input } from '@/shared/components/ui/Input';
import { ApiError } from '@/shared/lib/api-client';
import { confirm } from '@/shared/lib/confirm';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Contact,
  Download,
  FileSpreadsheet,
  Loader2,
  Upload,
  User,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { read, utils } from 'xlsx';
import {
  type CampaignImportRow,
  type ContactPickerRow,
  type RecipientFilterSelection,
  type RecipientsView,
  type TagOption,
  listCampaignContacts,
} from './api';
import { lerTextoDoCsv } from './codificacao-do-csv';

type Mode = 'system' | 'upload';

type AppliedFilter = NonNullable<RecipientsView['applied']>;

interface Props {
  selectedContacts: ContactPickerRow[];
  setSelectedContacts: React.Dispatch<React.SetStateAction<ContactPickerRow[]>>;
  /** Seleção por filtro ("todos os N") — guarda a regra, não os 30k objetos.
   *  Mutuamente exclusiva com selectedContacts: ativar uma limpa a outra. */
  filterSelection: RecipientFilterSelection | null;
  setFilterSelection: React.Dispatch<React.SetStateAction<RecipientFilterSelection | null>>;
  /** Lista de upload (CSV) — números crus, só na memória do wizard. NÃO grava no
   *  banco no upload; o backend materializa na finalização (e os que já são
   *  contato entram no fluxo normal). Persiste no rascunho; some ao sair sem salvar. */
  uploadRows: CampaignImportRow[];
  setUploadRows: React.Dispatch<React.SetStateAction<CampaignImportRow[]>>;
  tags: TagOption[];
  accessGroups: AccessGroupRow[];
  initialView?: RecipientsView;
  onViewChange?: (v: RecipientsView) => void;
  /** Nº de variáveis {{N}} do template escolhido (passo 2). 0 = template sem
   *  variáveis. Habilita o modelo de planilha + mapeamento por coluna. */
  templateVariableCount?: number;
}

/** Normaliza header pra casar "parametro_1", "Parametro 1", "PARAMETRO1" etc.
 *  NFD + strip de marcas de combinação cobre acento ("Parâmetro" → "parametro"). */
function normalizeHeader(h: string): string {
  return h
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

/** Limite de tamanho de um parâmetro de template HSM (a Meta corta/rejeita). */
const MAX_PARAM_LEN = 1024;

/** Sanitiza o valor de uma célula pra virar parâmetro de template: colapsa
 *  qualquer whitespace (quebra de linha/tab/espaços múltiplos que a Meta rejeita
 *  com 132000) em UM espaço, apara as pontas e limita o tamanho. Célula vazia
 *  vira '' (o chamador então NÃO inclui o slot → cai no fallback do passo 2). */
function sanitizeParam(s: string): string {
  return s.replace(/\s+/g, ' ').trim().slice(0, MAX_PARAM_LEN);
}

export function RecipientsStep({
  selectedContacts,
  setSelectedContacts,
  filterSelection,
  setFilterSelection,
  uploadRows,
  setUploadRows,
  tags,
  accessGroups,
  initialView,
  onViewChange,
  templateVariableCount,
}: Props) {
  const [mode, setMode] = useState<Mode | null>(initialView?.mode ?? null);
  const varCount = templateVariableCount ?? 0;

  // ── Modo "sistema": filtros (estilo Consulta — date inputs, multiselect) ──
  // Filtros são RASCUNHO; só viram busca ao clicar "Buscar" (applied). Sem
  // filtro instantâneo — evita request a cada tecla/clique.
  const [dateFrom, setDateFrom] = useState(initialView?.dateFrom ?? ''); // YYYY-MM-DD
  const [dateTo, setDateTo] = useState(initialView?.dateTo ?? '');
  const [filterTagIds, setFilterTagIds] = useState<number[]>(initialView?.filterTagIds ?? []);
  const [filterAccessGroupIds, setFilterAccessGroupIds] = useState<number[]>(
    initialView?.filterAccessGroupIds ?? [],
  );
  const [nameQuery, setNameQuery] = useState(initialView?.nameQuery ?? '');
  const [applied, setApplied] = useState<AppliedFilter | null>(initialView?.applied ?? null);
  // Tabela paginada: registros por página + página atual. 'all' (Todos) =
  // seleção-por-filtro (não materializa). Não persiste no rascunho (reabre em
  // 50/pág 1) — o `applied` é que traz os resultados de volta.
  const [pageSize, setPageSize] = useState<number | 'all'>(50);
  const [page, setPage] = useState(1);

  // Reporta a view pro pai (wizard) salvar no rascunho — reabre na mesma tela.
  useEffect(() => {
    onViewChange?.({
      mode,
      dateFrom,
      dateTo,
      filterTagIds,
      filterAccessGroupIds,
      nameQuery,
      applied,
    });
  }, [
    mode,
    dateFrom,
    dateTo,
    filterTagIds,
    filterAccessGroupIds,
    nameQuery,
    applied,
    onViewChange,
  ]);

  const resultsQ = useQuery({
    queryKey: ['campaign-contacts', applied, pageSize, page],
    queryFn: () =>
      listCampaignContacts({
        ...(applied?.q && { q: applied.q }),
        ...(applied && applied.tagIds.length > 0 && { tagIds: applied.tagIds }),
        ...(applied &&
          (applied.accessGroupIds?.length ?? 0) > 0 && {
            accessGroupIds: applied.accessGroupIds,
          }),
        ...(applied?.from && { lastInboundFrom: applied.from }),
        ...(applied?.to && { lastInboundTo: applied.to }),
        // 'all' (Todos): mostra os primeiros 200 (a seleção pega tudo via
        // filtro). Páginas numeradas: OFFSET estável (id ASC).
        limit: pageSize === 'all' ? 200 : pageSize,
        offset: pageSize === 'all' ? 0 : (page - 1) * pageSize,
      }),
    enabled: mode === 'system' && applied !== null,
    // Mantém a página anterior na tela enquanto busca a nova — sem flash de
    // "Buscando…/0 de 0" ao paginar (só pisca na 1ª busca).
    placeholderData: keepPreviousData,
  });

  // Date input é local; sufixar hora força timezone certa (lição da Consulta).
  const runSearch = (): void => {
    const q = nameQuery.trim();
    // Filtro mudou → seleção-por-filtro antiga não vale mais + volta pra pág 1.
    setFilterSelection(null);
    setPage(1);
    setApplied({
      tagIds: filterTagIds,
      accessGroupIds: filterAccessGroupIds,
      ...(q && { q }),
      ...(dateFrom && { from: new Date(`${dateFrom}T00:00:00`).toISOString() }),
      ...(dateTo && { to: new Date(`${dateTo}T23:59:59.999`).toISOString() }),
    });
  };
  const results = resultsQ.data?.contacts ?? [];
  const total = resultsQ.data?.total ?? 0;

  const selectedIds = useMemo(() => new Set(selectedContacts.map((c) => c.id)), [selectedContacts]);

  const toggleContact = (c: ContactPickerRow): void => {
    // Escolher um contato individual sai do modo "todos do filtro".
    setFilterSelection(null);
    setSelectedContacts((prev) =>
      prev.some((s) => s.id === c.id) ? prev.filter((s) => s.id !== c.id) : [...prev, c],
    );
  };

  const totalPages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(total / pageSize));

  // Esta página inteira já está selecionada? (modo explícito)
  const pageAllSelected = results.length > 0 && results.every((c) => selectedIds.has(c.id));

  // Checkbox do cabeçalho (igual /contatos). filterSelection ("todos do filtro")
  // também deixa o cabeçalho marcado.
  const headerChecked = filterSelection != null || pageAllSelected;

  // 'Todos' → seleção-por-filtro (tudo, resolvido no disparo, não materializa
  // 30k). Páginas numeradas → marca/desmarca ESTA página (explícito, preciso).
  const toggleHeader = (): void => {
    if (pageSize === 'all') {
      if (filterSelection) {
        setFilterSelection(null);
      } else if (applied) {
        setSelectedContacts([]);
        setFilterSelection({
          tagIds: applied.tagIds,
          // Rascunho salvo ANTES desta feature não tem accessGroupIds no JSON
          // persistido → coalesce pra [] (senão .length crasha / flui undefined).
          accessGroupIds: applied.accessGroupIds ?? [],
          count: total,
          ...(applied.q && { q: applied.q }),
          ...(applied.from && { from: applied.from }),
          ...(applied.to && { to: applied.to }),
        });
      }
      return;
    }
    setFilterSelection(null);
    if (pageAllSelected) {
      const pageIds = new Set(results.map((c) => c.id));
      setSelectedContacts((prev) => prev.filter((c) => !pageIds.has(c.id)));
    } else {
      setSelectedContacts((prev) => {
        const map = new Map(prev.map((c) => [c.id, c]));
        for (const c of results) map.set(c.id, c);
        return Array.from(map.values());
      });
    }
  };

  const changePageSize = (v: number | 'all'): void => {
    setPageSize(v);
    setPage(1);
  };

  // Contagem efetiva pro rodapé: filtro-server vence explícito; senão soma
  // contatos selecionados + linhas da lista de upload (modos exclusivos).
  const effectiveCount = filterSelection
    ? filterSelection.count
    : selectedContacts.length + uploadRows.length;

  // ── Modo "upload" ───────────────────────────────────────────────────
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadFileName, setUploadFileName] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // Preview da tabela do arquivo: colunas + 5-6 linhas + contagens.
  const [uploadPreview, setUploadPreview] = useState<{
    // Colunas exibidas com rótulo amigável (label) + chave pra ler da linha (raw).
    columns: { label: string; raw: string }[];
    rows: Record<string, unknown>[];
    total: number;
    ignored: number;
    failed: number;
    /** Nº de variáveis do template sem coluna correspondente na planilha
     *  (essas caem no valor do passo 2). */
    missingVarCols: number;
  } | null>(null);

  // Reconstrói o preview (estado local) a partir de uploadRows quando ele não
  // existe — cobre 2 casos: rascunho restaurado E volta ao passo 3 após trocar
  // de template (a lista é preservada e re-relacionada ao novo template). As
  // variáveis por linha são chaveadas por número do slot ({{N}}), então casam
  // com qualquer template: exibe só os slots que o template ATUAL usa
  // (≤ varCount); os capturados além disso ficam guardados e são ignorados no
  // envio. Roda a cada montagem (troca de passo remonta o componente).
  useEffect(() => {
    if (uploadRows.length > 0 && !uploadPreview) {
      const slots = new Set<number>();
      for (const r of uploadRows) {
        if (r.variables) for (const k of Object.keys(r.variables)) slots.add(Number(k));
      }
      const usedSlots = [...slots].filter((s) => s <= varCount).sort((a, b) => a - b);
      const columns: { label: string; raw: string }[] = [
        { label: 'Número de telefone', raw: 'telefone' },
        { label: 'Nome', raw: 'nome' },
        ...usedSlots.map((s) => ({ label: `Parâmetro ${s}`, raw: `param_${s}` })),
      ];
      let missingVarCols = 0;
      for (let n = 1; n <= varCount; n++) if (!slots.has(n)) missingVarCols++;
      setUploadPreview({
        columns,
        rows: uploadRows.slice(0, 6).map((r) => ({
          telefone: r.phone,
          nome: r.display_name,
          ...Object.fromEntries(
            usedSlots.map((s) => [`param_${s}`, r.variables?.[String(s)] ?? '']),
          ),
        })),
        total: uploadRows.length,
        ignored: 0,
        failed: 0,
        missingVarCols,
      });
    }
    // Roda na montagem (inclui volta ao passo 3). varCount atual vem via prop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Baixa um CSV modelo com as colunas do template: telefone, nome + uma coluna
  // por variável ({{N}} → parametro_N). Uma linha de exemplo ajuda o preenchimento.
  const downloadModel = (): void => {
    const headers = ['telefone', 'nome'];
    for (let n = 1; n <= varCount; n++) headers.push(`parametro_${n}`);
    const example = ['5511999999999', 'João'];
    for (let n = 1; n <= varCount; n++) example.push(`valor da variavel ${n}`);
    const csv = `${headers.join(',')}\n${example.join(',')}\n`;
    const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'modelo-campanha.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const parseFile = async (file: File): Promise<void> => {
    setUploadError(null);
    setUploadPreview(null);
    setUploadFileName(null);
    // Só CSV — leve de parsear no browser; XLSX (zip+xml) pode travar a aba em
    // listas grandes. O `accept` filtra no seletor; este guard cobre drag-drop.
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setUploadError('Use um arquivo .csv (exporte do Excel como CSV).');
      return;
    }
    try {
      const buffer = await file.arrayBuffer();
      const wb = read(lerTextoDoCsv(new Uint8Array(buffer)), { type: 'string' });
      const sheetName = wb.SheetNames[0];
      if (!sheetName) throw new Error('Arquivo sem planilhas');
      const sheet = wb.Sheets[sheetName];
      if (!sheet) throw new Error('Planilha vazia');
      const json = utils.sheet_to_json(sheet, { defval: '' }) as Record<string, unknown>[];
      if (json.length === 0) throw new Error('Planilha sem linhas');

      // Auto-detecta colunas de nome e telefone (heurística por header).
      const headers = Object.keys(json[0] ?? {});
      const nameCol = headers.find((h) => /nome|name/i.test(h));
      const phoneCol = headers.find((h) => /phone|whats|telefone|fone|celular/i.test(h));
      if (!phoneCol) {
        throw new Error('Não encontrei coluna de telefone (ex: "telefone", "phone", "celular").');
      }

      // Detecta TODAS as colunas de variável (parametro_N) presentes no arquivo,
      // independentemente do template atual — a mesma lista se relaciona com
      // qualquer template pelo número do slot ({{N}}). O que passar do que o
      // template atual usa fica guardado (pra se trocar de template) e é
      // ignorado no envio (buildMetaComponents usa só {{1}}..{{N}}). As colunas
      // ausentes caem no valor fixo do passo 2.
      const seenSlots = new Set<number>();
      const varCols: { slot: number; column: string }[] = [];
      for (const h of headers) {
        const m = /^parametro(\d+)$/.exec(normalizeHeader(h));
        if (!m) continue;
        const slot = Number(m[1]);
        if (slot < 1 || slot > 100 || seenSlots.has(slot)) continue;
        seenSlots.add(slot);
        varCols.push({ slot, column: h });
      }
      varCols.sort((a, b) => a.slot - b.slot);

      const rows: CampaignImportRow[] = [];
      let ignored = 0; // linha sem telefone
      let failed = 0; // telefone presente mas inválido (curto demais)
      for (const r of json) {
        const rawPhone = String(r[phoneCol] ?? '').replace(/\D/g, '');
        if (!rawPhone) {
          ignored++;
          continue;
        }
        if (rawPhone.length < 8) {
          failed++;
          continue;
        }
        const phone = rawPhone.startsWith('55') ? `+${rawPhone}` : `+55${rawPhone}`;
        const name = nameCol ? String(r[nameCol] ?? '').trim() : '';
        // Variáveis por linha: só entra o slot cuja célula tem valor. Célula em
        // branco é OMITIDA → no dispatcher o slot cai no fallback do passo 2
        // (coluna vence só quando há valor). Valor sanitizado (whitespace/limite).
        const variables: Record<string, string> = {};
        for (const vc of varCols) {
          const val = sanitizeParam(String(r[vc.column] ?? ''));
          if (val) variables[String(vc.slot)] = val;
        }
        rows.push({
          display_name: name || phone,
          phone,
          ...(Object.keys(variables).length > 0 && { variables }),
        });
      }
      if (rows.length === 0) throw new Error('Nenhum telefone válido encontrado no arquivo.');

      // Colunas exibidas na prévia: telefone + nome (se houver) + só os
      // parâmetros que o template ATUAL usa (slot ≤ varCount). Os capturados além
      // disso ficam na lista (pra relacionar se trocar de template), mas não
      // poluem a prévia. Valores sanitizados = o que será enviado.
      const usedVarCols = varCols.filter((vc) => vc.slot <= varCount);
      const columns: { label: string; raw: string }[] = [
        { label: 'Número de telefone', raw: phoneCol },
      ];
      if (nameCol) columns.push({ label: 'Nome', raw: nameCol });
      for (const vc of usedVarCols) columns.push({ label: `Parâmetro ${vc.slot}`, raw: vc.column });

      // Faltantes = slots 1..varCount do template atual que não têm coluna.
      const presentSlots = new Set(varCols.map((vc) => vc.slot));
      let missingVarCols = 0;
      for (let n = 1; n <= varCount; n++) if (!presentSlots.has(n)) missingVarCols++;

      // NÃO grava no banco: a lista fica só na memória do wizard. O backend
      // materializa na finalização (e os que já são contato entram no fluxo
      // normal). Escolher por arquivo é exclusivo de contatos/filtro.
      setSelectedContacts([]);
      setFilterSelection(null);
      setUploadRows(rows);
      setUploadFileName(file.name);
      setUploadPreview({
        columns,
        rows: json.slice(0, 6).map((r) => {
          const out: Record<string, string> = { [phoneCol]: String(r[phoneCol] ?? '') };
          if (nameCol) out[nameCol] = String(r[nameCol] ?? '').trim();
          for (const vc of usedVarCols) out[vc.column] = sanitizeParam(String(r[vc.column] ?? ''));
          return out;
        }),
        total: json.length,
        ignored,
        failed,
        missingVarCols,
      });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Falha ao ler arquivo');
    }
  };

  // ── Render: seletor de modo ─────────────────────────────────────────
  if (mode === null) {
    return (
      <div className="space-y-3">
        <p className="text-[12px] text-n-600">Como você quer escolher os destinatários?</p>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setMode('system')}
            className="flex flex-col items-center gap-2 rounded-md border border-n-200 bg-n-0 p-5 text-center hover:border-brand-300 hover:bg-brand-50"
          >
            <Contact size={26} className="text-brand-500" strokeWidth={1.75} />
            <span className="font-semibold text-[13px] text-n-900">Contatos do sistema</span>
            <span className="text-[11px] text-n-500">Filtre por período, etiqueta e nome.</span>
          </button>
          <button
            type="button"
            onClick={() => setMode('upload')}
            className="flex flex-col items-center gap-2 rounded-md border border-n-200 bg-n-0 p-5 text-center hover:border-brand-300 hover:bg-brand-50"
          >
            <Upload size={26} className="text-brand-500" strokeWidth={1.75} />
            <span className="font-semibold text-[13px] text-n-900">Upload da lista</span>
            <span className="text-[11px] text-n-500">Arraste ou selecione um CSV.</span>
          </button>
        </div>
        {effectiveCount > 0 && (
          <div className="rounded border border-n-200 bg-n-50 p-3 text-[12px]">
            <b>{effectiveCount.toLocaleString('pt-BR')}</b> destinatário(s) selecionado(s)
            {filterSelection ? ' (por filtro)' : ''}.
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={async () => {
          // Trocar de método zera a seleção — modos são exclusivos (upload vs
          // contatos/filtro), evita misturar e contar duplicado. Confirma antes
          // (modal do sistema) se há trabalho a perder — evita perda acidental
          // de lista importada / contatos selecionados.
          const hasWork =
            selectedContacts.length > 0 || filterSelection != null || uploadRows.length > 0;
          if (
            hasWork &&
            !(await confirm({
              title: 'Trocar forma de seleção?',
              description: 'Isso vai limpar a lista/seleção atual.',
              confirmText: 'Trocar',
              cancelText: 'Cancelar',
              variant: 'danger',
            }))
          ) {
            return;
          }
          setSelectedContacts([]);
          setFilterSelection(null);
          setUploadRows([]);
          setUploadPreview(null);
          setUploadFileName(null);
          // Remove também a lista de contatos exibida (resultados do filtro).
          setApplied(null);
          setPage(1);
          setMode(null);
        }}
        className="text-[12px] text-brand-600 hover:underline"
      >
        ← Trocar forma de seleção
      </button>

      {mode === 'system' && (
        <>
          {/* Filtros estilo Consulta: grid de campos (date range, nome, etiquetas) */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <FieldLabel>Período do último contato</FieldLabel>
              <div className="flex gap-2">
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </div>
            </div>
            <label className="flex flex-col gap-1.5">
              <FieldLabel>Nome do cliente</FieldLabel>
              <Input
                value={nameQuery}
                onChange={(e) => setNameQuery(e.target.value)}
                placeholder="Buscar por nome…"
              />
            </label>
            <MultiSelect
              label="Etiquetas"
              options={tags.map((t) => ({ value: t.id, label: t.name }))}
              values={filterTagIds}
              onChange={setFilterTagIds}
            />
            {accessGroups.length > 0 && (
              <MultiSelect
                label="Grupos de acesso"
                options={accessGroups.map((g) => ({ value: g.id, label: g.name }))}
                values={filterAccessGroupIds}
                onChange={setFilterAccessGroupIds}
              />
            )}
          </div>

          {/* Botões de ação dos filtros */}
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                // Limpa os campos de filtro E a tabela de resultados exibida
                // (`applied`) — a lista mostrada é o produto do filtro, então
                // some junto. A seleção de contatos tem o "limpar" próprio e não
                // é tocada aqui.
                setDateFrom('');
                setDateTo('');
                setFilterTagIds([]);
                setFilterAccessGroupIds([]);
                setNameQuery('');
                setApplied(null);
                setPage(1);
              }}
              className="font-semibold text-[12px] text-n-500 hover:text-n-700 hover:underline"
            >
              Limpar filtros
            </button>
            <button
              type="button"
              onClick={runSearch}
              disabled={resultsQ.isFetching}
              className="flex items-center gap-2 rounded-md bg-brand-500 px-4 py-2 font-semibold text-[12px] text-white hover:bg-brand-600 disabled:opacity-60"
            >
              {resultsQ.isFetching ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Contact size={14} />
              )}
              Buscar
            </button>
          </div>

          {/* Barra de seleção (igual /contatos): abaixo da busca, acima da
              tabela, só quando há seleção. */}
          {effectiveCount > 0 && (
            <div className="flex items-center justify-between rounded-md border border-brand-200 bg-brand-50 px-3 py-2">
              <span className="font-semibold text-[12px] text-brand-700">
                {effectiveCount.toLocaleString('pt-BR')} destinatário(s) selecionado(s)
                {filterSelection ? ' (por filtro)' : ''}
              </span>
              <button
                type="button"
                onClick={() => {
                  setSelectedContacts([]);
                  setFilterSelection(null);
                  setUploadRows([]);
                  setUploadPreview(null);
                  setUploadFileName(null);
                }}
                className="font-semibold text-[11px] text-brand-700 hover:underline"
              >
                limpar
              </button>
            </div>
          )}

          {/* Estado vazio — busca sem resultados. Caixa com borda (moldura igual
              à tabela), sem ícone, e fora da tabela (pra não renderizar
              cabeçalho + frase solta + rodapé "0 de 0"). */}
          {applied !== null && !resultsQ.isPending && resultsQ.isError && (
            <div className="rounded-md border border-danger-200 bg-danger-50 px-4 py-8 text-center">
              <p className="font-medium text-[13px] text-danger-700">Não foi possível carregar os contatos</p>
              <p className="mt-1 text-[12px] text-danger-600">
                {resultsQ.error instanceof ApiError
                  ? (resultsQ.error.problem.detail ?? resultsQ.error.problem.title)
                  : 'Tente de novo em instantes.'}
              </p>
            </div>
          )}
          {applied !== null && !resultsQ.isPending && !resultsQ.isError && results.length === 0 && (
            <div className="rounded-md border border-n-200 px-4 py-8 text-center">
              <p className="font-medium text-[13px] text-n-600">Nenhum contato com esses filtros</p>
              <p className="mt-1 text-[12px] text-n-400">
                Ajuste os filtros acima e clique em Buscar.
              </p>
            </div>
          )}

          {/* Resultados — tabela com checkbox no topo (igual /contatos). Só
              aparece quando há resultados ou está carregando. */}
          {applied !== null && (resultsQ.isPending || results.length > 0) && (
            <div className="rounded-md border border-n-200">
              <div
                className={`max-h-56 overflow-auto transition-opacity ${
                  resultsQ.isFetching && !resultsQ.isPending ? 'opacity-50' : ''
                }`}
              >
                <table className="w-full text-[12px]">
                  <thead className="sticky top-0 z-10 border-n-200 border-b bg-n-50 text-left">
                    <tr>
                      <th style={{ width: '34px' }} className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={headerChecked}
                          onChange={toggleHeader}
                          disabled={results.length === 0}
                          aria-label="Selecionar esta página"
                        />
                      </th>
                      <th className="px-3 py-2 font-bold text-[11px] text-n-500 uppercase tracking-[.05em]">
                        Nome
                      </th>
                      <th className="whitespace-nowrap px-3 py-2 text-right font-bold text-[11px] text-n-500 uppercase tracking-[.05em]">
                        WhatsApp
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultsQ.isPending ? (
                      <tr>
                        <td colSpan={3} className="py-8 text-center">
                          <Loader2 size={18} className="mx-auto animate-spin text-n-400" />
                        </td>
                      </tr>
                    ) : (
                      results.map((c) => {
                        const on = filterSelection != null || selectedIds.has(c.id);
                        return (
                          <tr
                            key={c.id}
                            className={`border-n-100 border-b last:border-b-0 hover:bg-n-50 ${
                              on ? 'bg-brand-50' : ''
                            }`}
                          >
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={on}
                                onChange={() => toggleContact(c)}
                                aria-label="Selecionar contato"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2">
                                <div className="flex h-[28px] w-[28px] flex-shrink-0 items-center justify-center rounded-pill bg-n-100 text-n-400">
                                  <User size={15} strokeWidth={1.5} />
                                </div>
                                <span className="font-semibold text-n-900">
                                  {c.display_name ?? 'Sem nome'}
                                </span>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-right text-n-600">
                              {c.primary_identifier?.value ?? ''}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Rodapé: registros por página + intervalo + navegação */}
              <div className="flex flex-wrap items-center justify-between gap-2 border-n-200 border-t bg-n-50 px-3 py-2 text-[11px] text-n-600">
                <label className="flex items-center gap-2">
                  Registros por página
                  <select
                    value={pageSize === 'all' ? 'all' : String(pageSize)}
                    onChange={(e) =>
                      changePageSize(e.target.value === 'all' ? 'all' : Number(e.target.value))
                    }
                    className="rounded border border-n-300 bg-n-0 px-1.5 py-1 text-[11px] text-n-700"
                  >
                    {[50, 100, 200, 500].map((s) => (
                      <option key={s} value={String(s)}>
                        {s}
                      </option>
                    ))}
                    <option value="all">Todos</option>
                  </select>
                </label>
                {pageSize === 'all' ? (
                  <span>{total.toLocaleString('pt-BR')} contato(s)</span>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <span className="mr-1">
                      {total === 0
                        ? '0 de 0'
                        : `${((page - 1) * pageSize + 1).toLocaleString('pt-BR')}–${Math.min(page * pageSize, total).toLocaleString('pt-BR')} de ${total.toLocaleString('pt-BR')}`}
                    </span>
                    <button
                      type="button"
                      aria-label="primeira página"
                      disabled={page <= 1}
                      onClick={() => setPage(1)}
                      className="rounded p-1 hover:bg-n-100 disabled:opacity-40"
                    >
                      <ChevronsLeft size={14} />
                    </button>
                    <button
                      type="button"
                      aria-label="página anterior"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className="rounded p-1 hover:bg-n-100 disabled:opacity-40"
                    >
                      <ChevronLeft size={14} />
                    </button>
                    <button
                      type="button"
                      aria-label="próxima página"
                      disabled={page >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      className="rounded p-1 hover:bg-n-100 disabled:opacity-40"
                    >
                      <ChevronRight size={14} />
                    </button>
                    <button
                      type="button"
                      aria-label="última página"
                      disabled={page >= totalPages}
                      onClick={() => setPage(totalPages)}
                      className="rounded p-1 hover:bg-n-100 disabled:opacity-40"
                    >
                      <ChevronsRight size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {mode === 'upload' && (
        <>
          {/* Modelo de planilha por template: quando o template tem variáveis,
              oferece o CSV com uma coluna por variável (parametro_N). */}
          {varCount > 0 && !uploadPreview && (
            <div className="flex items-start gap-3 rounded-md border border-brand-100 bg-brand-50 px-3 py-2.5">
              <FileSpreadsheet size={18} className="mt-0.5 flex-shrink-0 text-brand-600" />
              <div className="min-w-0 flex-1">
                <p className="text-[12px] text-n-700">
                  Você deve baixar o modelo CSV, clicando no botão ao lado, preenchê-lo com os dados
                  que deseja enviar por essa campanha e em seguida carregar o arquivo preenchido
                  abaixo.
                </p>
              </div>
              <button
                type="button"
                onClick={downloadModel}
                className="flex flex-shrink-0 items-center gap-1.5 rounded-md border border-brand-200 bg-n-0 px-2.5 py-1.5 font-semibold text-[12px] text-brand-700 hover:bg-brand-100"
              >
                <Download size={14} />
                Baixar modelo
              </button>
            </div>
          )}
          {/* Dropzone some quando há lista carregada — o preview ocupa o lugar. */}
          {!uploadPreview && (
            <>
              {/* biome-ignore lint/a11y/useKeyWithClickEvents: dropzone clicável padrão */}
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  const f = e.dataTransfer.files[0];
                  if (f) void parseFile(f);
                }}
                onClick={() => document.getElementById('campaign-upload-input')?.click()}
                className={`flex cursor-pointer flex-col items-center gap-2 rounded-md border-2 border-dashed px-4 py-8 text-center ${
                  dragOver ? 'border-brand-400 bg-brand-50' : 'border-n-300 bg-n-50'
                }`}
              >
                <Upload size={24} className="text-n-400" />
                <span className="font-semibold text-[13px] text-n-700">
                  Arraste ou clique para selecionar o arquivo CSV
                </span>
                <input
                  id="campaign-upload-input"
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void parseFile(f);
                    e.target.value = '';
                  }}
                />
              </div>
            </>
          )}

          {uploadError && (
            <div className="rounded border border-danger-200 bg-danger-50 px-3 py-2 text-[12px] text-danger-700">
              {uploadError}
            </div>
          )}

          {uploadPreview && (
            <div className="overflow-hidden rounded-md border border-n-200 bg-n-0">
              <div className="border-n-100 border-b px-3 py-2 text-[11px] text-n-500">
                Arquivo: <span className="font-mono text-n-700">{uploadFileName}</span>
              </div>
              {/* Preview: cabeçalho com as colunas do CSV + 6 linhas */}
              <div className="max-h-[260px] overflow-auto">
                <table className="w-full text-[12px]">
                  <thead className="sticky top-0 bg-n-50 text-left">
                    <tr>
                      {uploadPreview.columns.map((col) => (
                        <th
                          key={col.raw}
                          className="whitespace-nowrap px-3 py-2 font-bold text-[11px] text-n-600"
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {uploadPreview.rows.map((row, i) => (
                      <tr key={i} className="border-n-100 border-t odd:bg-n-50/40">
                        {uploadPreview.columns.map((col) => (
                          <td key={col.raw} className="whitespace-nowrap px-3 py-2 text-n-700">
                            {String(row[col.raw] ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* "+X registros" + Limpar */}
              <div className="flex items-center justify-center gap-3 border-n-100 border-t px-3 py-3">
                {uploadPreview.total > uploadPreview.rows.length && (
                  <span className="text-[13px] text-n-500">
                    +{(uploadPreview.total - uploadPreview.rows.length).toLocaleString('pt-BR')}{' '}
                    registros.
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => {
                    // Remove a lista (ela só vivia na memória — nada no banco).
                    setUploadRows([]);
                    setUploadPreview(null);
                    setUploadFileName(null);
                    setUploadError(null);
                  }}
                  className="rounded-pill bg-danger-500 px-3 py-1.5 font-semibold text-[11px] text-white hover:bg-danger-600"
                >
                  Limpar registros
                </button>
              </div>
              {/* Total / ignorados / falhas */}
              <div className="flex flex-wrap gap-x-8 gap-y-1 border-n-100 border-t bg-n-50 px-3 py-2 text-[12px] text-n-700">
                <span>
                  <b>Total de registros:</b> {uploadPreview.total.toLocaleString('pt-BR')}
                </span>
                <span>
                  <b>Registros ignorados:</b> {uploadPreview.ignored}
                </span>
                <span>
                  <b>Falhas:</b> {uploadPreview.failed}
                </span>
              </div>
            </div>
          )}

          {/* Aviso: algumas variáveis do template ficaram sem coluna na planilha
              → caem no valor do passo 2 (a tabela acima já mostra as que vieram). */}
          {uploadPreview &&
            varCount > 0 &&
            uploadPreview.missingVarCols > 0 &&
            uploadPreview.missingVarCols < varCount && (
              <div className="rounded-md border border-warning-500 bg-warning-50 px-3 py-2 text-[12px] text-warning-700">
                {uploadPreview.missingVarCols} variável(is) sem coluna na planilha — vão usar o
                valor preenchido no passo 2 (Template).
              </div>
            )}

          {/* Nenhuma coluna de variável na planilha → tudo cai no passo 2. */}
          {uploadPreview && varCount > 0 && uploadPreview.missingVarCols === varCount && (
            <div className="rounded-md border border-warning-500 bg-warning-50 px-3 py-2 text-[12px] text-warning-700">
              A planilha não tem colunas de variável (<span className="font-mono">parametro_1</span>
              … ). As variáveis vão usar o valor do passo 2 — baixe o modelo se quiser personalizar
              por linha.
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Componentes de filtro (mesmo visual da tela de Consulta) ──────────────
function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="font-semibold text-[11px] uppercase tracking-[0.05em] text-n-500">
      {children}
    </span>
  );
}

function MultiSelect<V extends string | number>({
  label,
  options,
  values,
  onChange,
}: {
  label: string;
  options: Array<{ value: V; label: string }>;
  values: V[];
  onChange: (next: V[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  // Posição fixed do dropdown (renderizado em portal) — calculada do rect do
  // trigger. Portal pra document.body escapa do overflow-y-auto do body do
  // modal, que recortava o dropdown atrás do footer (bug do z/clipping).
  const [coords, setCoords] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    const reposition = (): void => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setCoords({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    reposition();
    // capture=true pega o scroll do body do modal (que não borbulha pra window).
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    const handler = (e: MouseEvent): void => {
      const t = e.target as Node;
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(t) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(t)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      document.removeEventListener('mousedown', handler);
    };
  }, [open]);

  return (
    <div ref={wrapperRef} className="flex flex-col gap-1.5">
      <FieldLabel>{label}</FieldLabel>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex h-9 items-center justify-between gap-2 rounded-md border border-n-300 bg-n-0 px-3 text-left text-sm text-n-700 hover:border-n-400 focus:border-brand-500 focus:outline-none focus:ring-[3px] focus:ring-brand-500/25"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="truncate">
          {values.length === 0 ? (
            <span className="text-n-400">Selecionar…</span>
          ) : (
            `${values.length} selecionada${values.length === 1 ? '' : 's'}`
          )}
        </span>
        <span className={`text-n-400 transition ${open ? 'rotate-180' : ''}`}>▾</span>
      </button>
      {open &&
        coords &&
        createPortal(
          <div
            ref={dropdownRef}
            role="listbox"
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              width: coords.width,
              zIndex: 60,
            }}
            className="max-h-[220px] overflow-y-auto rounded-md border border-n-200 bg-n-0 py-1 shadow-md"
          >
            {options.length === 0 && (
              <div className="px-3 py-2 text-[12px] text-n-400">Nenhuma opção.</div>
            )}
            {options.map((o) => {
              const active = values.includes(o.value);
              return (
                <label
                  key={String(o.value)}
                  className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[13px] text-n-800 hover:bg-n-50"
                >
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={() =>
                      onChange(active ? values.filter((v) => v !== o.value) : [...values, o.value])
                    }
                  />
                  <span className="truncate">{o.label}</span>
                </label>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}
